import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { validateResponseDate, validateStringValue } from '../../../utils/common';
import Form from '../../../common/Form';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import TextArea from '../../../common/Form/TextArea';
import { addServiceRequestRemark } from '../../../actions/service_request';
import { TicketMilestoneEventLabels, TicketMilestoneEvents } from '../TicketController';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import { sendToastMessage } from '../../../actions/toast';
import TravelLogModal from './TravelLogModal';

const formName = 'addRemarkForm';

export default function ViewAllRemark({ ticketId, remarkList, callHistory }) {
    const [remarks, setRemarks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('system'); // 'system', 'manual', or 'travelLog'

    useEffect(() => {
        setRemarks(remarkList);
    }, [remarkList]);

    // Filter remarks based on active tab
    const filteredRemarks = useMemo(() => {
        if (!remarkList) return [];

        let filtered = remarkList;

        if (activeTab === 'manual') {
            filtered = remarkList.filter(item => [TicketMilestoneEvents.REMARK_ADDED, TicketMilestoneEvents.MILESTONE_SIGN_OFF_ACCEPTED].includes(item?.action));
        } else {
            filtered = remarkList.filter(item => ![TicketMilestoneEvents.REMARK_ADDED, TicketMilestoneEvents.MILESTONE_SIGN_OFF_ACCEPTED,].includes(item?.action));
        }

        return filtered;
    }, [remarkList, activeTab]);

    // Apply search to filtered remarks
    const displayRemarks = useMemo(() => {
        if (!searchTerm) return filteredRemarks;

        const searchKey = searchTerm.trim().toLowerCase();
        return filteredRemarks.filter((item) => {
            const createdBy = `${validateStringValue(item?.user?.full_name)}`;
            const remarkText = item?.remark;
            return (
                createdBy.toLowerCase().includes(searchKey) ||
                remarkText?.toLowerCase().includes(searchKey)
            );
        });
    }, [filteredRemarks, searchTerm]);

    const handleSearch = (e) => {
        const searchKey = e.target.value.toLowerCase();
        setSearchTerm(searchKey);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchTerm(''); // Clear search when switching tabs
    };

    return (
        <div className='relative h-full flex flex-col shadow-2xl overflow-hidden px-4 py-2'>
            <div className={`${activeTab === 'travelLog' ? '' : 'mb-4'}`}>
                {/* Tabs */}
                <div className={`flex ${activeTab === 'travelLog' ? '' : 'mb-4'} border-b border-gray-200`}>
                    <button
                        onClick={() => handleTabChange('system')}
                        className={`px-4 py-2 font-medium text-font12 transition-colors ${activeTab === 'system'
                            ? 'text-scogoprimary border-b-2 border-scogoprimary'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        System Messages
                    </button>
                    <button
                        onClick={() => handleTabChange('manual')}
                        className={`px-4 py-2 font-medium text-font12 transition-colors ${activeTab === 'manual'
                            ? 'text-scogoprimary border-b-2 border-scogoprimary'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Manual Remarks
                    </button>
                    <button
                        onClick={() => handleTabChange('travelLog')}
                        className={`px-4 py-2 font-medium text-font12 transition-colors ${activeTab === 'travelLog'
                            ? 'text-scogoprimary border-b-2 border-scogoprimary'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Travel Log
                    </button>
                </div>

                {/* Search bar - only show for system and manual tabs */}
                {activeTab !== 'travelLog' && (
                    <div className='relative'>
                        <input
                            onChange={handleSearch}
                            value={searchTerm}
                            className='w-full py-2 px-4 border-2 border-gray-300 rounded-md text-font11 focus:outline-none focus:border-scogoprimary transition-colors'
                            placeholder='Search by remark or user...'
                        />
                        <span className='material-icons absolute right-3 top-2 text-gray-400 text-2xl'>
                            search
                        </span>
                    </div>
                )}
            </div>

            {/* Travel Log Tab */}
            {activeTab === 'travelLog' ? (
                <div className='overflow-y-auto flex-auto rounded-md'>
                    <TravelLogModal ticketId={ticketId} />
                </div>
            ) : (
                <>
                    {/* Table header - only show for system messages */}
                    {activeTab === 'system' && (
                        <div className='flex items-center px-3 py-2 bg-gray-100 rounded-md mb-2'>
                            <div className='text-font12 px-2 text-scogoprimary font-medium w-4/12'>Date & Time</div>
                            <div className='text-font12 px-2 text-scogoprimary font-medium w-6/12'>Remark</div>
                            <div className='text-font12 px-2 text-scogoprimary font-medium w-2/12'>Added By</div>
                        </div>
                    )}

                    <div className='overflow-y-auto flex-auto mb-4 rounded-md border border-gray-200'>
                        {Array.isArray(displayRemarks) && displayRemarks.length > 0 ? (
                            activeTab === 'manual' ? (
                                // Timeline view for manual remarks
                                <div className='p-4'>
                                    <div className='relative'>
                                        {displayRemarks.map((item, i) => {
                                            const createdAt = moment(new Date(validateResponseDate(item?.created_at))).format('MMM DD, YYYY - hh:mm:ss A');
                                            const createdBy = `${validateStringValue(item?.user?.display_name)}`;
                                            const userInitials = createdBy.split(' ').map(name => name[0]).join('').toUpperCase();
                                            const eventLabel = TicketMilestoneEventLabels[item?.action] || '';
                                            const remarkText = item?.remark;

                                            return (
                                                <div key={i} className='relative mb-6 last:mb-0'>
                                                    {/* Timeline line */}
                                                    {i !== displayRemarks.length - 1 && (
                                                        <div className='absolute left-5 top-12 w-0.5 h-full bg-scogoprimary/20'></div>
                                                    )}

                                                    <div className='flex items-start space-x-4'>
                                                        {/* Avatar */}
                                                        <div className='w-10 h-10 bg-scogoprimary rounded-full flex items-center justify-center text-white font-semibold text-font11 shadow-lg flex-shrink-0 relative z-10'>
                                                            {userInitials}
                                                        </div>

                                                        {/* Remark card */}
                                                        <div className='flex-1 bg-white rounded-lg shadow-md border border-gray-100 p-4'>
                                                            {/* Header */}
                                                            <div className='flex items-center justify-between mb-3'>
                                                                <span className='font-semibold text-scogobgsky text-font12'>{createdBy}</span>
                                                                <span className='text-font11 text-gray-500 bg-gray-50 px-2 py-1 rounded'>{createdAt}</span>
                                                            </div>

                                                            {/* Remark text */}
                                                            <div className='text-font11 text-gray-700 leading-relaxed'>
                                                                {eventLabel && (
                                                                    <span className='font-medium text-scogoprimary mr-1'>{eventLabel}:</span>
                                                                )}
                                                                {remarkText}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                // Table view for system messages
                                displayRemarks.map((item, i) => {
                                    const createdAt = moment(new Date(validateResponseDate(item?.created_at))).format('YYYY-MM-DD HH:mm:ss');
                                    const createdBy = `${validateStringValue(item?.user?.display_name)}`;

                                    let remarkText = item?.remark;
                                    if (item?.action) {
                                        const eventLabel = TicketMilestoneEventLabels[item?.action] || '';
                                        remarkText = `${eventLabel ? `${eventLabel}: ` : ''} ${item?.remark || ''}`;
                                    }

                                    return (
                                        <div key={i} className={`flex items-center px-3 py-3 ${i % 2 !== 0 ? 'bg-gray-50' : ''} hover:bg-blue-50 transition-colors`}>
                                            <div className='text-font11 py-2 px-2 w-4/12 break-all text-gray-700'>{createdAt}</div>
                                            <div className='text-font11 py-2 px-2 w-6/12 break-all'>{remarkText}</div>
                                            <div className='text-font11 py-2 px-2 w-2/12 break-all text-scogoprimary font-medium'>{createdBy}</div>
                                        </div>
                                    );
                                })
                            )
                        ) : (
                            <div className='flex items-center justify-center h-32 text-gray-500'>
                                {searchTerm ? 'No remarks match your search' : `No ${activeTab === 'manual' ? 'manual remarks' : 'system messages'} available`}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Add Remark Form - only show for system and manual tabs */}
            {activeTab !== 'travelLog' && (
                <div className='flex-initial w-full bg-white rounded-md px-3 py-3 border border-gray-200'>
                    <AddremarkForm ticketId={ticketId} callHistory={callHistory} />
                </div>
            )}
        </div>
    );
}

const AddremarkForm = ({ ticketId, callHistory }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const methods = useForm({ defaultValues: {}, mode: 'all' });

    const submit = (formValues) => {
        if (!formValues.remark || !formValues.remark.trim()) {
            dispatch(sendToastMessage({ status: 'warning', message: 'Please add a remark to add a remark' }));
            return;
        };

        dispatch(
            addServiceRequestRemark({
                ticketId,
                payload: {
                    remark: formValues.remark
                },
                formName,
                closeModal: true,
                callHistory
            })
        );
        methods.reset();
    };

    return (
        <div className='bg-white'>
            <Form
                methods={methods}
                onSubmit={submit}
                className='w-full'
                showCancelButton={false}
            >
                <div className='flex gap-3 items-end'>
                    <div className='flex-1'>
                        <TextArea
                            className='w-full resize-none border border-gray-300 rounded px-3 py-2 focus:border-scogoprimary focus:outline-none transition-colors text-font11 placeholder-gray-400'
                            label=''
                            name='remark'
                            type='text'
                            placeholder='Add a remark about this ticket...'
                            rows={3}
                        />
                    </div>

                    <div className='flex-shrink-0'>
                        <ButtonScogoPrimary
                            textOrComponent={
                                <div className='flex items-center gap-1'>
                                    <span className="material-icons text-font11">send</span>
                                    <span className='text-font11'>Add Remark</span>
                                </div>
                            }
                            onClick={methods.handleSubmit(submit)}
                            loading={formButtonLoading[formName]}
                            buttonClass='px-4 py-2'
                        />
                    </div>
                </div>
            </Form>
        </div>
    );
}